import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  TextField,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
} from "@mui/material";
import { Add as AddIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { Save, Plus, Trash2, Play, ChevronDown, Eye } from "react-feather";
import Table from "../../components/global/Table";
import {
  listVendorConfigs,
  getVendorConfig,
  createVendorConfig,
  updateVendorConfig,
  testVendorQuery,
  describeTable,
  previewHardDelete,
} from "../../services/customTapsVendorConfigs";

const MSSQL_LOGO = "https://assets-global.website-files.com/6064b31ff49a2d31e0493af1/61120e2c9af151acfd631cd0_MSSQL.svg";

const TAP_TYPES = [
  {
    id: "mssql",
    name: "Microsoft SQL Server",
    description: "MSSQL database extraction via SSH tunnel to Snowflake",
    logo: MSSQL_LOGO,
  },
];

const TapAdmin = () => {
  // Accordion state
  const [expandedType, setExpandedType] = useState(null);
  const [expandedVendorId, setExpandedVendorId] = useState(null);

  // Vendor list state
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sortedColumn, setSortedColumn] = useState({ column: "vendor_name", isAsc: true });

  // Edit state
  const [editConfig, setEditConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [loadingVendor, setLoadingVendor] = useState(false);

  // Query testing
  const [testCustomer, setTestCustomer] = useState("vena");
  const [testingQuery, setTestingQuery] = useState(null);
  const [testResult, setTestResult] = useState(null);

  // Stream column metadata
  const [streamColumns, setStreamColumns] = useState({}); // { streamName: { columns: [], primary_keys: [] } }
  const [loadingColumns, setLoadingColumns] = useState({});

  // Hard delete preview
  const [previewingDelete, setPreviewingDelete] = useState(null);
  const [deletePreview, setDeletePreview] = useState({});

  const handleSortColumn = (columnName) => {
    setSortedColumn((prev) => ({
      column: columnName,
      isAsc: prev.column === columnName ? !prev.isAsc : true,
    }));
  };

  const loadConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listVendorConfigs();
      setConfigs(data);
    } catch (err) {
      setError(err.error || "Failed to load vendor configs");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTapType = (typeId) => {
    if (expandedType === typeId) {
      setExpandedType(null);
      setExpandedVendorId(null);
      setEditConfig(null);
    } else {
      setExpandedType(typeId);
      setExpandedVendorId(null);
      setEditConfig(null);
      loadConfigs();
    }
  };

  const handleToggleVendor = async (id) => {
    if (expandedVendorId === id) {
      setExpandedVendorId(null);
      setEditConfig(null);
      setTestResult(null);
    } else {
      setExpandedVendorId(id);
      setEditConfig(null);
      setTestResult(null);
      setError(null);
      setLoadingVendor(true);
      try {
        const data = await getVendorConfig(id);
        setEditConfig({
          ...data,
          streams: typeof data.streams === "string" ? JSON.parse(data.streams) : data.streams,
          hard_delete: typeof data.hard_delete === "string" ? JSON.parse(data.hard_delete) : data.hard_delete,
        });
        setIsCreate(false);
      } catch (err) {
        setError(err.error || "Failed to load vendor config");
      } finally {
        setLoadingVendor(false);
      }
    }
  };

  const handleCreate = () => {
    setEditConfig({
      vendor_name: "",
      display_name: "",
      streams: [],
      hard_delete: [],
    });
    setIsCreate(true);
    setExpandedVendorId("new");
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const streams = editConfig.streams.map((s) => ({
        ...s,
        query: s.sync_mode === "incremental_merge" ? s.query : `SELECT * FROM ${s.name}`,
        dedupe_keys: s.bulk_pk ? [s.bulk_pk] : [],
      }));
      const payload = {
        vendor_name: editConfig.vendor_name,
        display_name: editConfig.display_name || editConfig.vendor_name,
        streams,
        hard_delete: editConfig.hard_delete,
      };
      if (isCreate) {
        await createVendorConfig(payload);
        setSuccess("Vendor config created");
      } else {
        await updateVendorConfig(editConfig.id, payload);
        setSuccess("Vendor config saved");
      }
      setTimeout(() => {
        setSuccess(null);
        setExpandedVendorId(null);
        setEditConfig(null);
        loadConfigs();
      }, 1000);
    } catch (err) {
      setError(err.error || "Failed to save vendor config");
    } finally {
      setSaving(false);
    }
  };

  // Stream helpers
  const updateStream = (idx, field, value) => {
    const updated = [...editConfig.streams];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditConfig({ ...editConfig, streams: updated });
    if (field === "sync_mode" && value === "incremental_merge" && updated[idx].name) {
      fetchColumns(updated[idx].name, idx);
    }
  };
  const addStream = () => {
    setEditConfig({
      ...editConfig,
      streams: [...editConfig.streams, { name: "", query: "", sync_mode: "full_refresh_overwrite" }],
    });
  };
  const removeStream = (idx) => {
    setEditConfig({ ...editConfig, streams: editConfig.streams.filter((_, i) => i !== idx) });
  };

  // Hard delete helpers
  const updateHardDelete = (idx, field, value) => {
    const updated = [...editConfig.hard_delete];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditConfig({ ...editConfig, hard_delete: updated });
  };
  const addHardDelete = () => {
    setEditConfig({
      ...editConfig,
      hard_delete: [...editConfig.hard_delete, { source_query: "", destination_query: "", destination_table: "", pk_column: "" }],
    });
  };
  const removeHardDelete = (idx) => {
    setEditConfig({ ...editConfig, hard_delete: editConfig.hard_delete.filter((_, i) => i !== idx) });
  };

  // Query testing
  const handleTestQuery = async (queryKey, query) => {
    setTestingQuery(queryKey);
    setTestResult(null);
    try {
      const result = await testVendorQuery(editConfig.vendor_name, query, testCustomer);
      setTestResult({ key: queryKey, ...result });
    } catch (err) {
      setTestResult({ key: queryKey, error: err.error || "Query test failed" });
    } finally {
      setTestingQuery(null);
    }
  };

  const fetchColumns = async (streamName, idx) => {
    if (!streamName || loadingColumns[idx]) return;
    if (streamColumns[streamName] && !streamColumns[streamName].error) return;
    setLoadingColumns((prev) => ({ ...prev, [idx]: true }));
    try {
      const stream = editConfig?.streams?.[idx];
      const result = await describeTable(streamName, testCustomer, stream?.query);
      setStreamColumns((prev) => ({ ...prev, [streamName]: result }));
      // Auto-set primary key if inferred and not already set
      if (result.primary_keys?.length > 0 && editConfig) {
        const stream = editConfig.streams[idx];
        if (!stream.bulk_pk) {
          updateStream(idx, "bulk_pk", result.primary_keys[0]);
        }
      }
    } catch (err) {
      console.error("Failed to fetch columns for", streamName, err);
      setStreamColumns((prev) => ({ ...prev, [streamName]: { columns: [], primary_keys: [], error: true } }));
    } finally {
      setLoadingColumns((prev) => ({ ...prev, [idx]: false }));
    }
  };

  const handlePreviewDelete = async (idx, hd) => {
    setPreviewingDelete(idx);
    setDeletePreview((prev) => ({ ...prev, [idx]: undefined }));
    try {
      const result = await previewHardDelete({
        source_query: hd.source_query,
        destination_query: hd.destination_query,
        destination_table: hd.destination_table,
        pk_column: hd.pk_column,
        customer: testCustomer,
      });
      setDeletePreview((prev) => ({ ...prev, [idx]: result }));
    } catch (err) {
      setDeletePreview((prev) => ({ ...prev, [idx]: { error: err.error || "Preview failed" } }));
    } finally {
      setPreviewingDelete(null);
    }
  };

  const getSortedConfigs = () => {
    return [...configs].sort((a, b) => {
      const aVal = a[sortedColumn.column] || "";
      const bVal = b[sortedColumn.column] || "";
      return sortedColumn.isAsc ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  };

  const renderTestResult = (queryKey) => {
    if (testResult?.key !== queryKey) return null;
    return (
      <Box sx={{ mt: 0.5 }}>
        {testResult.error ? (
          <Alert severity="error" sx={{ fontSize: 11, py: 0 }}>{testResult.error}</Alert>
        ) : (
          <Paper variant="outlined" sx={{ p: 1, maxHeight: 150, overflow: "auto" }}>
            <Typography fontSize={11} color="success.main" mb={0.5}>{testResult.count} row(s) returned</Typography>
            {testResult.rows?.length > 0 && (
              <Box sx={{ fontSize: 10, fontFamily: "monospace", whiteSpace: "pre-wrap", overflow: "auto" }}>
                {testResult.columns?.join(" | ")}{"\n"}
                {testResult.rows.map((row) => testResult.columns.map((col) => String(row[col] ?? "")).join(" | ")).join("\n")}
              </Box>
            )}
          </Paper>
        )}
      </Box>
    );
  };

  const renderTestButton = (queryKey, query) => (
    <Button
      size="small" variant="contained"
      disabled={testingQuery === queryKey || !query}
      onClick={() => handleTestQuery(queryKey, query)}
      startIcon={testingQuery === queryKey ? <CircularProgress size={12} color="inherit" /> : <Play size={12} />}
      sx={{ textTransform: "none", fontSize: 11, minWidth: 70, alignSelf: "stretch", background: "#2D49FD", "&:hover": { opacity: 0.9 }, "&.Mui-disabled": { background: "#b0b8c9" } }}
    >
      Test
    </Button>
  );

  const renderEditor = () => {
    if (!editConfig) return null;
    return (
      <Box sx={{ pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <Stack spacing={2.5}>
          {/* Top fields */}
          <Stack direction="row" spacing={2}>
            <TextField size="small" label="Vendor Name" value={editConfig.vendor_name}
              onChange={(e) => setEditConfig({ ...editConfig, vendor_name: e.target.value })}
              sx={{ flex: 1, "& input": { fontSize: 13 } }} />
            <TextField size="small" label="Test Customer" value={testCustomer}
              onChange={(e) => { setTestCustomer(e.target.value); setStreamColumns({}); }}
              helperText="For query testing"
              sx={{ width: 150, "& input": { fontSize: 13 } }} />
          </Stack>

          {/* Streams */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#090E47" }}>Streams ({editConfig.streams.length})</Typography>
              <Button size="small" startIcon={<Plus size={12} />} onClick={addStream} sx={{ textTransform: "none", fontSize: 11 }}>Add Stream</Button>
            </Stack>
            {editConfig.streams.map((stream, idx) => {
              const meta = streamColumns[stream.name];
              const cols = meta?.columns || [];
              const pks = meta?.primary_keys || [];
              const isIncremental = stream.sync_mode === "incremental_merge";
              return (
              <Accordion
                key={idx}
                disableGutters
                elevation={0}
                onChange={() => {}}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px !important",
                  mb: 1,
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  sx={{ px: 1.5, py: 0, minHeight: 40, "&:hover": { backgroundColor: "rgba(45,73,253,0.02)" } }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#090E47", flex: 1, fontFamily: "monospace" }}>
                      {stream.name || `Stream ${idx + 1}`}
                    </Typography>
                    {isIncremental && <Chip label="incremental" size="small" sx={{ fontSize: 10, height: 18 }} />}
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeStream(idx); }} color="error"><Trash2 size={12} /></IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, pb: 1.5 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" label="Name" value={stream.name}
                        onChange={(e) => updateStream(idx, "name", e.target.value)}
                        sx={{ flex: 1, "& input": { fontSize: 12 } }} />
                      <TextField size="small" label="Sync Mode" value={stream.sync_mode || ""} select
                        onChange={(e) => updateStream(idx, "sync_mode", e.target.value)}
                        sx={{ width: 220, "& .MuiSelect-select": { fontSize: 12 } }}
                        SelectProps={{ native: true }}
                      >
                        <option value="full_refresh_overwrite">Full Refresh (Overwrite)</option>
                        <option value="incremental_merge">Incremental (Merge)</option>
                      </TextField>
                    </Stack>
                    {isIncremental ? (
                      <>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <TextField size="small" label="Query" value={stream.query} multiline minRows={2}
                            onChange={(e) => updateStream(idx, "query", e.target.value)}
                            sx={{ flex: 1, "& textarea": { fontSize: 12, fontFamily: "monospace" } }} />
                          {renderTestButton(`stream-${idx}`, stream.query)}
                        </Stack>
                        {renderTestResult(`stream-${idx}`)}
                      </>
                    ) : (
                      <Typography sx={{ fontSize: 11, fontFamily: "monospace", color: "#6c757d", py: 0.5 }}>
                        Query: SELECT * FROM {stream.name || "<table>"}
                      </Typography>
                    )}
                    {isIncremental && (
                      <Stack direction="row" spacing={1}>
                        <TextField size="small" label="Cursor Field" value={stream.cursor_field || ""}
                          onChange={(e) => updateStream(idx, "cursor_field", e.target.value)}
                          sx={{ flex: 1, "& input": { fontSize: 12, fontFamily: "monospace" } }}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField size="small" label="Primary Key" value={stream.bulk_pk || ""}
                          onChange={(e) => updateStream(idx, "bulk_pk", e.target.value)}
                          sx={{ flex: 1, "& input": { fontSize: 12, fontFamily: "monospace" } }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
              );
            })}
          </Box>

          {/* Hard Deletes */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#090E47" }}>Hard Deletes ({editConfig.hard_delete.length})</Typography>
              <Button size="small" startIcon={<Plus size={12} />} onClick={addHardDelete} sx={{ textTransform: "none", fontSize: 11 }}>Add Hard Delete</Button>
            </Stack>
            {editConfig.hard_delete.map((hd, idx) => {
              const preview = deletePreview[idx];
              const deleteRatio = preview && preview.destination_count > 0 ? preview.to_delete_count / preview.destination_count : 0;
              const severityColor = !preview || preview.error ? undefined : preview.to_delete_count === 0 ? "#4caf50" : deleteRatio > 0.01 ? "#f44336" : "#ff9800";
              return (
              <Accordion
                key={idx}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px !important",
                  mb: 1,
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  sx={{ px: 1.5, py: 0, minHeight: 40, "&:hover": { backgroundColor: "rgba(45,73,253,0.02)" } }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#090E47", flex: 1, fontFamily: "monospace" }}>
                      {hd.destination_table || `Hard Delete ${idx + 1}`}
                    </Typography>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeHardDelete(idx); }} color="error"><Trash2 size={12} /></IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, pb: 1.5 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" label="Destination Table" value={hd.destination_table}
                        onChange={(e) => updateHardDelete(idx, "destination_table", e.target.value)}
                        sx={{ flex: 1, "& input": { fontSize: 12, fontFamily: "monospace" } }} />
                      <TextField size="small" label="PK Column" value={hd.pk_column}
                        onChange={(e) => updateHardDelete(idx, "pk_column", e.target.value)}
                        sx={{ flex: 1, "& input": { fontSize: 12 } }} />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <TextField size="small" label="Source Query (MSSQL)" value={hd.source_query} multiline minRows={2}
                        onChange={(e) => updateHardDelete(idx, "source_query", e.target.value)}
                        sx={{ flex: 1, "& textarea": { fontSize: 12, fontFamily: "monospace" } }} />
                      {renderTestButton(`hd-${idx}`, hd.source_query)}
                    </Stack>
                    {renderTestResult(`hd-${idx}`)}
                    <TextField size="small" label="Destination Query (Snowflake) — use {destination_schema}" value={hd.destination_query} multiline minRows={2}
                      onChange={(e) => updateHardDelete(idx, "destination_query", e.target.value)}
                      sx={{ "& textarea": { fontSize: 12, fontFamily: "monospace" } }} />
                    <Box>
                      <Button
                        size="small" variant="outlined"
                        disabled={previewingDelete === idx || !hd.source_query || !hd.destination_query}
                        onClick={() => handlePreviewDelete(idx, hd)}
                        startIcon={previewingDelete === idx ? <CircularProgress size={12} color="inherit" /> : <Eye size={12} />}
                        sx={{ textTransform: "none", fontSize: 11, minWidth: 130, borderColor: "#2D49FD", color: "#2D49FD", "&:hover": { borderColor: "#2D49FD", backgroundColor: "rgba(45,73,253,0.04)" } }}
                      >
                        Preview Deletes
                      </Button>
                    </Box>
                    {preview && (
                      <Paper variant="outlined" sx={{ p: 1.5, borderColor: severityColor || "#e0e0e0" }}>
                        {preview.error ? (
                          <Alert severity="error" sx={{ fontSize: 11, py: 0 }}>{preview.error}</Alert>
                        ) : (
                          <Stack spacing={1}>
                            <Box sx={{ display: "flex", gap: 3, fontSize: 12 }}>
                              <span>Source: <b>{preview.source_count?.toLocaleString()}</b></span>
                              <span>Destination: <b>{preview.destination_count?.toLocaleString()}</b></span>
                              <span style={{ color: severityColor, fontWeight: 700 }}>Would delete: {preview.to_delete_count?.toLocaleString()}</span>
                            </Box>
                            {preview.to_delete_count > 0 && preview.to_delete_ids?.length > 0 && (
                              <Box sx={{ maxHeight: 120, overflow: "auto", fontSize: 11, fontFamily: "monospace", background: "#fafafa", borderRadius: 1, p: 1 }}>
                                {preview.to_delete_ids.map((id, i) => <div key={i}>{String(id)}</div>)}
                                {preview.to_delete_count > 100 && (
                                  <Typography sx={{ fontSize: 10, color: "#999", mt: 0.5 }}>...and {(preview.to_delete_count - 100).toLocaleString()} more</Typography>
                                )}
                              </Box>
                            )}
                          </Stack>
                        )}
                      </Paper>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
              );
            })}
          </Box>

          {/* Save button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
            <Button
              variant="contained" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} /> : <Save size={14} />}
              sx={{
                fontSize: 11, fontWeight: 500, height: 32, px: 3, textTransform: "none", borderRadius: "6px",
                background: "#2D49FD", "&:hover": { opacity: 0.9 },
              }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Helmet title="Datajolt | Custom Taps" />
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", margin: "12px 8px", height: "45px" }}>
          <h4 style={{ marginBottom: 0 }}>
            <b style={{ color: "#090E47", marginRight: "10px" }}>Custom Taps</b>
          </h4>
        </Box>
        <hr style={{ margin: "0 8px" }} />
        <Box sx={{ p: 1 }}>
          {TAP_TYPES.map((tapType) => (
            <Accordion
              key={tapType.id}
              expanded={expandedType === tapType.id}
              onChange={() => handleToggleTapType(tapType.id)}
              disableGutters
              elevation={0}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px !important",
                mb: 1.5,
                "&:before": { display: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 2.5,
                  py: 0.5,
                  "&:hover": { backgroundColor: "rgba(45,73,253,0.02)" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: "8px", backgroundColor: "rgba(45,73,253,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img src={tapType.logo} alt={tapType.name} style={{ width: 42, height: 42 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#090E47" }}>{tapType.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{tapType.description}</Typography>
                  </Box>
                  {expandedType === tapType.id && configs.length > 0 && (
                    <Button
                      variant="contained" size="small"
                      onClick={(e) => { e.stopPropagation(); handleCreate(); }}
                      sx={{ fontSize: 11, fontWeight: 500, height: 28, px: 2, textTransform: "none", borderRadius: "6px", background: "#2D49FD", "&:hover": { opacity: 0.9 } }}
                    >
                      Add Vendor <AddIcon sx={{ ml: "5px", fontSize: 14 }} />
                    </Button>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
                {loading && configs.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : configs.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography sx={{ fontSize: 13, color: "#6c757d", mb: 2 }}>No vendor configs yet</Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreate} sx={{ fontSize: 11, textTransform: "none", borderRadius: "6px" }}>
                      Add Vendor
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    {/* New vendor form */}
                    {isCreate && expandedVendorId === "new" && (
                      <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: "8px", borderColor: "#2D49FD" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#090E47", mb: 1.5 }}>New Vendor</Typography>
                        {renderEditor()}
                      </Paper>
                    )}
                    {getSortedConfigs().map((cfg) => {
                      const streams = typeof cfg.streams === "string" ? JSON.parse(cfg.streams) : cfg.streams;
                      const hd = typeof cfg.hard_delete === "string" ? JSON.parse(cfg.hard_delete) : cfg.hard_delete;
                      const isExpanded = expandedVendorId === cfg.id;
                      return (
                        <Accordion
                          key={cfg.id}
                          expanded={isExpanded}
                          onChange={() => handleToggleVendor(cfg.id)}
                          disableGutters
                          elevation={0}
                          sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "6px !important",
                            mb: 1,
                            "&:before": { display: "none" },
                            overflow: "hidden",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                            sx={{ px: 2, py: 0, minHeight: 48, "&:hover": { backgroundColor: "rgba(45,73,253,0.02)" } }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#090E47", flex: 1 }}>{cfg.vendor_name}</Typography>
                              <Chip label={`${streams?.length || 0} streams`} size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />
                              <Chip label={`${hd?.length || 0} hard deletes`} size="small" color="secondary" variant="outlined" sx={{ fontSize: 11 }} />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2, pb: 2 }}>
                            {isExpanded && loadingVendor && (
                              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                <CircularProgress size={24} />
                              </Box>
                            )}
                            {isExpanded && !loadingVendor && editConfig && renderEditor()}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default TapAdmin;
